import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Section,
} from '../../components/ndf/ui/Champ';
import { NavigationMois } from '../../components/ndf/ui/NavigationMois';
import { moisCourant, useMoisDeSession } from '../../hooks/useMoisDeSession';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';

/**
 * Tickets restaurant : la vue de calcul et la commande Swile (specs §H3).
 *
 * « Gertrude calcule, la RH valide » (R-TKT-07). Le tableau montre TOUT le
 * détail — jours ouvrés, jours déduits avec leur cause, régularisations, report
 * négatif — parce que c'est le justificatif qu'un contrôle URSSAF vient
 * chercher. Un total sans son détail ne se défend pas.
 */

const dateFr = (iso: string) => iso.split('-').reverse().join('/');

const LIBELLE_STATUT: Record<string, string> = {
  calcule: 'Calculé',
  valide: 'Validé par la RH',
  exporte: 'Exporté',
};

export function CongesTicketsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  // Le mois survit à un aller-retour vers un autre écran (Lot 7, point 10).
  const [mois, setMois] = useMoisDeSession('tickets', moisCourant());
  const [detaille, setDetaille] = useState<string | null>(null);
  const [attenteDepliee, setAttenteDepliee] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'tickets', mois],
    queryFn: () => congesApi.tickets.mois(mois),
  });

  const rafraichir = () => qc.invalidateQueries({ queryKey: ['conges', 'tickets', mois] });

  // Le refus se dit AVANT le clic : un bouton actif qui répond « 0 ligne »
  // laisse croire à un mois sans bénéficiaire. La borne vient du serveur, seul
  // à la connaître.
  const premier = data?.premier_mois ?? null;
  const refusCalcul = !premier
    ? 'Le premier mois de calcul n’est pas renseigné : aucun calcul possible. Renseignez-le dans Paramétrage.'
    : mois < premier
      ? `Ce mois est antérieur au premier mois de calcul (${premier}) : il a été traité dans Airtable.`
      : null;

  const calculer = useMutation({
    mutationFn: () => congesApi.tickets.calculer(mois),
    onSuccess: (r) => {
      toast.success(`${r.lignes} ligne(s) calculée(s).`);
      rafraichir();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'Le calcul a échoué.'),
  });

  const valider = useMutation({
    mutationFn: () => congesApi.tickets.valider(mois),
    onSuccess: (r) => {
      toast.success(`${r.lignes} ligne(s) validée(s). Le mois est figé.`);
      rafraichir();
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'La validation a échoué.'),
  });

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;

  const lignes = data?.lignes ?? [];
  const statut = data?.statut;
  const fige = statut === 'valide' || statut === 'exporte';

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">Tickets restaurant</h1>
          <p className="text-sm text-nuit/60">
            {statut
              ? `${LIBELLE_STATUT[statut] ?? statut} : ${data?.total ?? 0} titre(s) au total.`
              : 'Ce mois n’a pas encore été calculé.'}
          </p>
        </div>
        <NavigationMois valeur={mois} onChange={setMois} />
      </header>

      {refusCalcul && <p className="text-sm text-corail">{refusCalcul}</p>}

      <div className="flex flex-wrap gap-2">
        <BoutonSecondaire
          type="button"
          onClick={() => calculer.mutate()}
          disabled={calculer.isPending || fige || Boolean(refusCalcul)}
        >
          {calculer.isPending ? 'Calcul…' : lignes.length ? 'Recalculer' : 'Calculer le mois'}
        </BoutonSecondaire>
        {lignes.length > 0 && !fige && (
          <BoutonPrincipal type="button" onClick={() => valider.mutate()} disabled={valider.isPending}>
            Valider le mois
          </BoutonPrincipal>
        )}
        {fige && (
          <a href={congesApi.tickets.lienSwile(mois)} download>
            <BoutonPrincipal type="button">Télécharger la commande Swile</BoutonPrincipal>
          </a>
        )}
      </div>

      {fige && (
        <p className="rounded-lg border border-vert-fond/60 bg-white p-3 text-sm text-vert">
          Le mois est figé : un congé saisi maintenant partira en régularisation du mois suivant.
        </p>
      )}

      {/* Les demandes encore en attente qui touchent le mois. Elles n'entrent
          PAS dans le calcul — le droit au ticket se calcule sur les faits, et
          une approbation tardive se régularise le mois suivant. Mais commander
          sans le savoir, c'est commander des titres à reprendre : on relance le
          valideur, ou on commande en connaissance de cause. */}
      {(data?.en_attente ?? []).length > 0 && (
        <div className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left font-medium text-jaune"
            onClick={() => setAttenteDepliee((v) => !v)}
            aria-expanded={attenteDepliee}
          >
            <span>
              Attention : {data!.en_attente.length} demande(s) en attente chevauchent ce mois. Elles
              ne sont pas comptées dans le calcul.
            </span>
            <span aria-hidden="true">{attenteDepliee ? '▾' : '▸'}</span>
          </button>
          {attenteDepliee && (
            <ul className="mt-2 space-y-1 text-nuit/80">
              {data!.en_attente.map((d) => (
                <li key={d.id}>
                  {d.salarie} : {d.type_libelle}, du {dateFr(d.date_debut)} au {dateFr(d.date_fin)} (
                  {d.nb_jours} j),{' '}
                  {d.statut === 'attente_manager' ? 'chez le manager' : 'chez la RH'}
                </li>
              ))}
            </ul>
          )}
          {attenteDepliee && (
            <p className="mt-2 text-nuit/60">
              Une demande approuvée après la validation du mois se régularise sur le mois suivant,
              ligne à ligne.{' '}
              <Link to="/conges/a-valider" className="font-medium underline">
                Voir les demandes à valider
              </Link>
            </p>
          )}
        </div>
      )}

      {/* La complétude est dite AVANT la commande : découvrir une fiche
          incomplète au moment de commander fait perdre le créneau d'envoi. */}
      {(data?.incompletes ?? []).length > 0 && (
        <div className="rounded-lg border border-corail-fond/60 border-l-4 border-l-corail-fond bg-white p-3 text-sm">
          <p className="font-medium text-corail">
            Des fiches sont incomplètes : la commande Swile sera refusée.
          </p>
          <ul className="mt-1 text-nuit/80">
            {(data?.incompletes ?? []).map((i) => (
              <li key={i.nom}>
                {i.nom} : {i.manques.join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {lignes.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-nuit/60">
              <tr>
                <th className="px-3 py-2 font-medium">Salarié</th>
                <th className="px-3 py-2 text-right font-medium">Jours ouvrés</th>
                <th className="px-3 py-2 text-right font-medium">Déduits</th>
                <th className="px-3 py-2 text-right font-medium">Régularisation</th>
                <th className="px-3 py-2 text-right font-medium">Report négatif</th>
                <th className="px-3 py-2 text-right font-medium">Titres</th>
                <th className="px-3 py-2 font-medium">Détail</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-nuit">
                    {l.salarie.prenom} {l.salarie.nom}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-nuit/70">{l.jours_ouvres}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-nuit/70">
                    {l.jours_ouvres - l.jours_droit}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      l.regularisation < 0 ? 'text-corail' : l.regularisation > 0 ? 'text-vert' : 'text-nuit/40'
                    }`}
                  >
                    {l.regularisation !== 0 ? l.regularisation : '-'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-nuit/70">
                    {l.solde_negatif_reporte !== 0 ? `-${l.solde_negatif_reporte}` : '-'}
                    {l.solde_negatif_restant > 0 && (
                      <span className="block text-xs text-corail">
                        reste {l.solde_negatif_restant}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-nuit">
                    {l.tickets}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-xs text-violet underline"
                      onClick={() => setDetaille(detaille === l.id ? null : l.id)}
                    >
                      {detaille === l.id ? 'Masquer' : 'Voir'}
                    </button>
                    {detaille === l.id && (
                      <ul className="mt-1 space-y-0.5 text-xs text-nuit/70">
                        {l.detail.deductions.map((d) => (
                          <li key={d.jour}>
                            {dateFr(d.jour)} : {d.cause}
                          </li>
                        ))}
                        {l.detail.regularisations.map((r) => (
                          <li key={r.jour} className="text-jaune">
                            {r.cause}
                          </li>
                        ))}
                        {l.detail.deductions.length === 0 &&
                          l.detail.regularisations.length === 0 && <li>Aucune déduction.</li>}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Section titre="Ce que dit ce tableau" teinte="ciel">
        <p className="text-sm text-nuit/80">
          Un jour ouvre droit à un titre si le temps de travail couvre entièrement la pause
          déjeuner. Une journée entière d’absence n’ouvre aucun droit ; une{' '}
          <strong>demi-journée non plus</strong> (convention Kestio) ; en mi-temps thérapeutique,
          c’est la case « pause déjeuner couverte » du rythme qui décide.
        </p>
        <p className="mt-2 text-sm text-nuit/80">
          Les écarts constatés après le versement d’un mois se régularisent sur le suivant, ligne à
          ligne. Un mois qui deviendrait négatif ne verse rien et reporte son reliquat : on ne
          reprend pas des titres déjà remis.
        </p>
      </Section>
    </div>
  );
}
